import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import JsBarcode from "jsbarcode";
import { getChargeTypeCode } from "../pages/const";
import { getTenantNameDisplay } from "../utils/tenantName";

// Register fonts if needed
Font.register({
  family: "Times New Roman",
  src: "https://db.onlinewebfonts.com/t/32441506567156636049eb850b53f02a.ttf",
});

// Conversion constant: 1mm = 2.834645669pt
const mmToPt = (mm) => mm * 2.834645669;

// Create styles for barcode sheet (A4: 210mm × 297mm)
const styles = StyleSheet.create({
  page: {
    fontFamily: "Times-Roman",
  },
  pageGold: {
    fontFamily: "Times-Roman",
  },
  container: {
    flex: 1,
  },
  header: {
    alignItems: "center",
    marginBottom: 15,
  },
  title: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#333",
    marginBottom: 5,
  },
  subtitle: {
    fontSize: 10,
    color: "#666",
    textAlign: "center",
  },
  // Tags on A4: shifted right + slightly less top margin (match label sheet position)
  squareGrid: {
    paddingTop: mmToPt(9),
    paddingLeft: mmToPt(55),
    paddingBottom: mmToPt(5),
    paddingRight: 0,
  },
  squareGridGold: {
    paddingTop: mmToPt(6),
    paddingLeft: mmToPt(55),
    paddingBottom: mmToPt(7),
    paddingRight: 0,
  },
  // Main square container Silver (55mm x 14mm)
  squareContainer: {
    width: mmToPt(55), // 155.91pt
    height: mmToPt(14), // 39.69pt
    // border: "0.1pt solid #7d6c6c",
    // borderRadius: 8,
    marginBottom: mmToPt(2.5), // 7.09pt (gap between squares)
    flexDirection: "row",
    overflow: "hidden",
  },
  // Gold: 55mm x 18mm, no marginBottom
  squareContainerGold: {
    width: mmToPt(55),
    height: mmToPt(18),
    // border: "0.1pt solid #7d6c6c",
    // borderRadius: 8,
    flexDirection: "row",
    overflow: "hidden",
  },
  // Left half (27.5mm) - Barcode area (Silver)
  leftHalf: {
    width: mmToPt(27.5), // 77.95pt
    height: "100%",
    // borderTopLeftRadius: 6,
    // borderBottomLeftRadius: 6,
    flexDirection: "column",
    justifyContent: "flex-start",
    paddingTop: 2,
    paddingHorizontal: 1,
  },
  // Gold: increased top padding
  leftHalfGold: {
    width: mmToPt(27.5),
    height: "100%",
    // borderTopLeftRadius: 6,
    // borderBottomLeftRadius: 6,
    flexDirection: "column",
    justifyContent: "flex-start",
    paddingTop: 4,
    paddingHorizontal: 1,
  },
  // Black partition line
  partitionLine: {
    width: 0.1,
    height: "100%",
    // backgroundColor: "#000000",
  },
  // Right half (27.5mm) - Data area (Silver)
  rightHalf: {
    width: mmToPt(27.5), // 77.95pt
    height: "100%",
    // borderTopRightRadius: 6,
    // borderBottomRightRadius: 6,
    flexDirection: "column",
    justifyContent: "flex-start",
    paddingTop: 2,
    paddingHorizontal: 1,
    alignItems: "center",
  },
  // Gold: increased top padding
  rightHalfGold: {
    width: mmToPt(27.5),
    height: "100%",
    // borderTopRightRadius: 6,
    // borderBottomRightRadius: 6,
    flexDirection: "column",
    justifyContent: "flex-start",
    paddingTop: 4,
    paddingHorizontal: 1,
    alignItems: "center",
  },
  // Fixed width container for alignment - 22mm = ~62px (62pt in PDF)
  alignContainer: {
    width: 62, // 22mm converted to points (approximately 62pt)
    alignSelf: "center",
    paddingLeft: 18, // Move values to the right so HUID appears centered
  },
  // Row layout for label-value pairs in columns
  weightRow: {
    flexDirection: "row",
    marginBottom: 0.5,
  },
  // Label with fixed width for alignment
  weightLabel: {
    width: 15, // Fixed width for labels to align in column
  },
  // Value with margin for spacing
  weightValue: {
    marginLeft: 0, // Reduced gap between label and value
  },
  // Silver right half: value column (GW, NW, size) fixed width, right-aligned like Gold
  weightValueSilver: {
    width: 18,
    textAlign: "left",
  },
  // Text styles – headings (MANGLIK, Manglik Gold). manglikText = Silver "Manglik Gold"
  manglikText: {
    fontSize: 6,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 1,
    color: "#000",
  },
  manglikTextGold: {
    fontSize: 6,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 1,
    color: "#000",
  },
  tagText: {
    fontSize: 6.5,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 1,
    color: "#000",
  },
  tagTextGold: {
    fontSize: 6.5,
    fontWeight: "bold",
    textAlign: "center",
    // marginBottom: 1,
    color: "#000",
  },
  // Row below barcode: same width as barcode (80%), tag_no left aligns with barcode start, % right aligns with barcode end
  tagLbrRow: {
    width: "80%",
    alignSelf: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    // marginBottom: 1,
  },
  chargesText: {
    fontSize: 5,
    fontWeight: "bold",
    textAlign: "center",
    color: "#000",
    lineHeight: 0.8,
  },
  chargesTextGold: {
    fontSize: 5,
    fontWeight: "bold",
    textAlign: "center",
    color: "#000",
    lineHeight: 0.8,
  },
  rightSideText: {
    fontSize: 6.5,
    fontWeight: "bold",
    marginBottom: 0.5,
    color: "#000",
    lineHeight: 0.8,
  },
  rightSideTextGold: {
    fontSize: 6.5,
    fontWeight: "bold",
    marginBottom: 0.5,
    color: "#000",
    lineHeight: 0.8,
  },
  rightSideSmallText: {
    fontSize: 6.5,
    fontWeight: "bold",
    marginBottom: 0.5,
    color: "#000",
    lineHeight: 0.8,
  },
  rightSideSmallTextGold: {
    fontSize: 6.5,
    fontWeight: "bold",
    marginBottom: 0.5,
    color: "#000",
    lineHeight: 0.8,
  },
  // Barcode image styling
  barcodeImage: {
    width: "90%",
    height: 10,
    marginBottom: 1,
  },
  barcodeText: {
    fontSize: 6,
    fontWeight: "bold",
    color: "#000",
    textAlign: "center",
  },
  footer: {
    position: "absolute",
    bottom: 10,
    left: 10,
    right: 10,
    textAlign: "center",
    fontSize: 8,
    color: "#999",
  },
});

/** Normalize list row / show API product into barcode label fields */
function normalizeBarcodeProduct(raw) {
  if (!raw) return {};

  const gwtRaw = raw.gwt ?? raw.GWT ?? raw.gWt ?? raw.current_gwt ?? "";
  const lwtRaw =
    raw.lwt_customer ??
    raw.LWt_customer ??
    raw.LWT_customer ??
    raw.lWtCustomer ??
    raw.lWt ??
    "";
  const nwtRaw = raw.nwt ?? raw.NWT ?? "";

  const gwtNum = parseFloat(gwtRaw);
  const lwtNum = parseFloat(lwtRaw);
  const nwtNum = parseFloat(nwtRaw);

  let NWt = null;
  if (!Number.isNaN(nwtNum)) {
    NWt = nwtNum;
  } else if (!Number.isNaN(gwtNum)) {
    NWt = Number.isNaN(lwtNum) ? gwtNum : gwtNum - lwtNum;
  }

  return {
    tag_no: raw.tag_no ?? raw.tagNo ?? "",
    GWT: Number.isNaN(gwtNum) ? null : gwtNum,
    LWt_customer: Number.isNaN(lwtNum) ? null : lwtNum,
    NWt,
    short_code: raw.short_code ?? raw.shortCode ?? "",
    supplier_name:
      raw.supplier_name ?? raw.supplier_company_name ?? raw.supplier ?? "",
    size: raw.size ?? "",
    touch: raw.touch ?? raw.purity ?? raw.Touch ?? "",
    huid: raw.huid ?? "",
    lbr_percent: raw.lbr_percent ?? raw.lbrPercentage ?? raw.LBR ?? "",
    lbr_amt: raw.lbr_amt ?? raw.lbrAmt ?? raw.LBR_amt ?? "",
    charges: Array.isArray(raw.charges) ? raw.charges : [],
  };
}

const formatWeight = (value) => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num.toFixed(3) : "";
};

// Function to generate barcode as base64 image
const generateBarcodeImage = (value) => {
  try {
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, value, {
      format: "CODE128",
      width: 1,
      height: 25,
      displayValue: false,
      fontSize: 8,
      fontOptions: "bold",
      margin: 0,
      background: "transparent",
    });
    return canvas.toDataURL("image/png");
  } catch (error) {
    console.error("Error generating barcode:", error);
    return null;
  }
};

// Barcode PDF component
const BarcodePDF = ({
  selectedItems,
  barcodeType = "Silver",
  startPosition = 1,
}) => {
  const tenantNameDisplay = getTenantNameDisplay();
  // Silver: 10 squares; Gold: 9 squares
  const totalSquares =
    barcodeType === "Silver"
      ? 10
      : barcodeType === "Gold"
        ? 9
        : selectedItems.length;

  // Build items: (startPosition - 1) leading nulls, then selected items, then trailing nulls
  const itemsToShow = [];
  const start = Math.max(1, Math.min(Number(startPosition) || 1, totalSquares));
  const skip = start - 1;

  if (barcodeType === "Silver" || barcodeType === "Gold") {
    for (let i = 0; i < totalSquares; i++) {
      if (i < skip) {
        itemsToShow.push(null);
      } else {
        const idx = i - skip;
        itemsToShow.push(
          idx < selectedItems.length ? selectedItems[idx] : null,
        );
      }
    }
  } else {
    itemsToShow.push(...selectedItems);
  }

  const isGold = barcodeType === "Gold";
  const gridStyle = isGold ? styles.squareGridGold : styles.squareGrid;
  const containerStyle = isGold
    ? styles.squareContainerGold
    : styles.squareContainer;
  const pageStyle = isGold ? styles.pageGold : styles.page;
  const leftHalfStyle = isGold ? styles.leftHalfGold : styles.leftHalf;
  const rightHalfStyle = isGold ? styles.rightHalfGold : styles.rightHalf;
  const manglikStyle = isGold ? styles.manglikTextGold : styles.manglikText;
  const tagStyle = isGold ? styles.tagTextGold : styles.tagText;
  const chargesStyle = isGold ? styles.chargesTextGold : styles.chargesText;
  const rightTextStyle = isGold
    ? styles.rightSideTextGold
    : styles.rightSideText;
  const rightSmallStyle = isGold
    ? styles.rightSideSmallTextGold
    : styles.rightSideSmallText;

  return (
    <Document>
      <Page size="A4" style={pageStyle}>
        <View style={styles.container}>
          {/* Header */}

          {/* Square Grid */}
          <View style={gridStyle}>
            {itemsToShow.map((item, index) => {
              const productData = normalizeBarcodeProduct(
                item?.barcodeData?.product || item,
              );
              const barcodeValue =
                productData.tag_no ||
                item?.tagNo ||
                item?.id ||
                `ITEM${index + 1}`;
              const barcodeImage = item
                ? generateBarcodeImage(barcodeValue)
                : null;

              return (
                <View
                  key={`square-${index}`}
                  style={[
                    containerStyle,
                    // Silver only: remove margin-bottom from last square
                    !isGold &&
                      index === totalSquares - 1 && { marginBottom: 0 },
                  ]}
                >
                  {/* Left Half - Barcode Area */}
                  <View style={leftHalfStyle}>
                    {item ? (
                      <>
                        {isGold && <Text style={manglikStyle}>MANGLIK</Text>}

                        {/* Barcode */}
                        {barcodeImage && (
                          <Image
                            src={barcodeImage}
                            style={{
                              width: "80%",
                              height: isGold ? 8 : 6,
                              marginBottom: 1,
                              alignSelf: "center",
                            }}
                          />
                        )}

                        {/* Tag no (left) | LBR% or LBR_amt (right) */}
                        <View style={styles.tagLbrRow}>
                          <Text style={[tagStyle, { textAlign: "left" }]}>
                            {barcodeValue}
                          </Text>
                          <Text style={[tagStyle, { textAlign: "right" }]}>
                            {(() => {
                              const lbr = productData?.lbr_percent;
                              const lbrAmt = productData?.lbr_amt;
                              const hasPct = lbr != null && Number(lbr) !== 0;
                              if (hasPct) return `${Number(lbr)}%`;
                              if (lbrAmt != null && Number(lbrAmt) !== 0)
                                return String(Number(lbrAmt));
                              return "";
                            })()}
                          </Text>
                        </View>

                        {isGold ? (
                          /* Gold: Charges: ShortCode/pcs x rate = total */
                          <View
                            style={{ flex: 1, justifyContent: "flex-start" }}
                          >
                            {productData?.charges?.length > 0 &&
                              productData.charges
                                .slice(0, 4)
                                .map((charge, chargeIndex) => {
                                  const code = getChargeTypeCode(
                                    charge.charge_type,
                                  );
                                  const pcs =
                                    charge.pcs != null ? Number(charge.pcs) : 1;
                                  const rate =
                                    charge.rate != null
                                      ? Number(charge.rate)
                                      : 0;
                                  const total =
                                    charge.amount != null
                                      ? Number(charge.amount)
                                      : 0;
                                  const str = [code, `${pcs}x${rate}=${total}`]
                                    .filter(Boolean)
                                    .join("/");
                                  return (
                                    <Text
                                      key={chargeIndex}
                                      style={chargesStyle}
                                    >
                                      {str}
                                    </Text>
                                  );
                                })}
                          </View>
                        ) : (
                          /* Silver: short_code (left) and size (right), same alignment as tag_no | LBR% */
                          <View style={styles.tagLbrRow}>
                            <Text style={[tagStyle, { textAlign: "left" }]}>
                              {productData?.short_code || ""}
                            </Text>
                            <Text style={[tagStyle, { textAlign: "right" }]}>
                              {productData?.size ?? ""}
                            </Text>
                          </View>
                        )}
                      </>
                    ) : null}
                  </View>

                  {/* Black Partition Line */}
                  <View style={styles.partitionLine} />

                  {/* Right Half - Product Data */}
                  <View style={rightHalfStyle}>
                    {item ? (
                      <>
                        {isGold ? (
                          <>
                            <View style={styles.alignContainer}>
                              {/* GW */}
                              <View style={styles.weightRow}>
                                <Text
                                  style={[rightTextStyle, styles.weightLabel]}
                                >
                                  GW
                                </Text>
                                <Text
                                  style={[rightTextStyle, styles.weightValue]}
                                >
                                  {formatWeight(productData.GWT)}
                                </Text>
                              </View>

                              {/* LW */}
                              <View style={styles.weightRow}>
                                <Text
                                  style={[rightTextStyle, styles.weightLabel]}
                                >
                                  LW
                                </Text>
                                <Text
                                  style={[rightTextStyle, styles.weightValue]}
                                >
                                  {productData.LWt_customer
                                    ? formatWeight(productData.LWt_customer)
                                    : ""}
                                </Text>
                              </View>

                              {/* NW = GW - LW */}
                              <View style={styles.weightRow}>
                                <Text
                                  style={[rightTextStyle, styles.weightLabel]}
                                >
                                  NW
                                </Text>
                                <Text
                                  style={[rightTextStyle, styles.weightValue]}
                                >
                                  {formatWeight(productData.NWt)}
                                </Text>
                              </View>

                              {/* Supplier name and size (one line) */}
                              <View style={styles.weightRow}>
                                <Text
                                  style={[rightSmallStyle, styles.weightLabel]}
                                >
                                  {productData?.supplier_name || ""}
                                </Text>
                                <Text
                                  style={[rightSmallStyle, styles.weightValue]}
                                >
                                  {productData?.size ?? ""}
                                </Text>
                              </View>

                              {/* Short code and touch (one line) */}
                              <View style={styles.weightRow}>
                                <Text
                                  style={[rightTextStyle, styles.weightLabel]}
                                >
                                  {productData?.short_code || ""}
                                </Text>
                                <Text
                                  style={[rightTextStyle, styles.weightValue]}
                                >
                                  {(() => {
                                    const touchValue = productData?.touch;
                                    if (!touchValue) return "";
                                    return parseFloat(touchValue)
                                      .toString()
                                      .replace(/\.00$/, "");
                                  })()}
                                </Text>
                              </View>
                            </View>

                            {/* HUID - new line, centred */}
                            <View
                              style={{
                                alignSelf: "stretch",
                                marginTop: 1,
                                alignItems: "center",
                              }}
                            >
                              <Text
                                style={[
                                  rightSmallStyle,
                                  { textAlign: "center" },
                                ]}
                              >
                                {productData?.huid &&
                                productData?.huid !== "N/A"
                                  ? productData.huid.replace(/##/g, "/")
                                  : ""}
                              </Text>
                            </View>
                          </>
                        ) : (
                          /* Silver: Manglik Gold, GW/NW, supplierName + size; centred like Gold */
                          <View
                            style={{
                              alignSelf: "center",
                              alignItems: "center",
                              width: 52,
                            }}
                          >
                            <Text
                              style={[
                                manglikStyle,
                                { marginBottom: 2, alignSelf: "center" },
                              ]}
                            >
                              {tenantNameDisplay}
                            </Text>
                            <View
                              style={[
                                styles.weightRow,
                                { width: 38, justifyContent: "space-between" },
                              ]}
                            >
                              <Text
                                style={[rightTextStyle, styles.weightLabel]}
                              >
                                GW
                              </Text>
                              <Text
                                style={[
                                  rightTextStyle,
                                  styles.weightValueSilver,
                                ]}
                              >
                                {formatWeight(productData.GWT)}
                              </Text>
                            </View>
                            <View
                              style={[
                                styles.weightRow,
                                { width: 38, justifyContent: "space-between" },
                              ]}
                            >
                              <Text
                                style={[rightTextStyle, styles.weightLabel]}
                              >
                                NW
                              </Text>
                              <Text
                                style={[
                                  rightTextStyle,
                                  styles.weightValueSilver,
                                ]}
                              >
                                {formatWeight(productData.NWt)}
                              </Text>
                            </View>
                          </View>
                        )}
                      </>
                    ) : null}
                  </View>
                </View>
              );
            })}
          </View>

          {/* Footer */}
        </View>
      </Page>
    </Document>
  );
};

export default BarcodePDF;
